import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel,
    Select, TextField, Tooltip, Typography
} from "@mui/material";
import {
    Search as IconSearch, RestartAlt as IconRestart, Visibility as IconVisibility
} from "@mui/icons-material";
import moment from "moment";
import { useLocation } from "react-router-dom";

import * as bitacoraService from "../../services/bitacoraService";
import DataGridBase from "../../components/common/DataGridBase";
import { PaperComponentDraggable } from "../../components/common/PaperComponentDraggable";
import { interpretarOperacion, ENTIDADES_NOMBRES, extraerIdRegistro } from "../../services/bitacoraOperacionesHelper";

const hoyFecha = () => moment().format("YYYY-MM-DD");

const FILTROS_INICIALES = () => ({
    idRegistro: "",
    tipoAccion: "",
    tabla: "",
    usuario: "",
    fechaInicio: `${hoyFecha()}T00:00`,
    fechaFin: `${hoyFecha()}T23:59`,
});

const OPCIONES_TIPO_ACCION = [
    { id: "", label: "(Todas las operaciones)" },
    { id: "ANULACION", label: "Anulaciones de Registros" },
    { id: "ACTIVACION", label: "Activaciones de Registros" },
    { id: "CONSOLIDACION", label: "Consolidación / Desconsolidación de Pagos" },
    { id: "ASIGNACION", label: "Asignación / Desvinculación de Comprobantes" },
    { id: "VALIDACION", label: "Validación / Desvalidación de Sesiones" },
    { id: "CIERRE_PERIODO", label: "Cierres y Reaperturas de Periodo" },
    { id: "DESCUENTO", label: "Descuentos y Tarifas" },
    { id: "ALTA", label: "Altas de Registros" },
    { id: "BAJA", label: "Bajas / Eliminaciones" },
];

const OPCIONES_ENTIDADES = [
    { id: "", label: "(Todas las entidades)" },
    { id: "comprobante", label: "Comprobantes" },
    { id: "comprobantepago", label: "Pagos de Comprobante" },
    { id: "terapiaperiodopago", label: "Pagos de Periodos de Terapia" },
    { id: "terapiaperiodo", label: "Periodos de Terapia" },
    { id: "sesion", label: "Sesiones Clínicas" },
    { id: "periodo", label: "Periodos Académicos" },
    { id: "terapia", label: "Terapias" },
    { id: "participante", label: "Participantes" },
    { id: "trabajador", label: "Trabajadores / Terapeutas" },
    { id: "descuento", label: "Descuentos" },
];

const OperacionesBitacora = () => {
    const location = useLocation();
    const [cargando, setCargando] = useState(false);
    const [results, setResults] = useState([]);
    const [filtros, setFiltros] = useState(FILTROS_INICIALES);
    const [detalleFila, setDetalleFila] = useState(null);

    // Si viene con parametros en la URL (?id=14&tabla=comprobante)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idParam = params.get("id");
        const tablaParam = params.get("tabla");
        if (idParam || tablaParam) {
            setFiltros((prev) => ({
                ...prev,
                idRegistro: idParam || prev.idRegistro,
                tabla: tablaParam || prev.tabla,
            }));
        }
    }, [location.search]);

    const alCambiarFechaInicio = (nuevaFechaInicio) => {
        // La fecha de fin por defecto es la fecha de inicio hasta las 23:59
        if (nuevaFechaInicio && nuevaFechaInicio.includes("T")) {
            const fechaParte = nuevaFechaInicio.split("T")[0];
            setFiltros((prev) => ({
                ...prev,
                fechaInicio: nuevaFechaInicio,
                fechaFin: `${fechaParte}T23:59`,
            }));
        } else {
            setFiltros((prev) => ({
                ...prev,
                fechaInicio: nuevaFechaInicio,
            }));
        }
    };

    const fetchResults = useCallback(async (filtrosActuales = filtros) => {
        setCargando(true);

        const paramsApi = {
            tabla: filtrosActuales.tabla || undefined,
            usuario: filtrosActuales.usuario ? filtrosActuales.usuario.trim() : undefined,
            limite: 200,
        };

        // Conversion a UTC para consultar FechaHoraUtc en la base de datos
        if (filtrosActuales.fechaInicio) {
            const mInicio = moment(filtrosActuales.fechaInicio);
            if (mInicio.isValid()) {
                paramsApi.fechaInicio = mInicio.utc().format("YYYY-MM-DDTHH:mm:ss");
            }
        }

        if (filtrosActuales.fechaFin) {
            const mFin = moment(filtrosActuales.fechaFin);
            if (mFin.isValid()) {
                paramsApi.fechaFin = mFin.utc().format("YYYY-MM-DDTHH:mm:ss");
            }
        }

        // Si se especificó ID de registro, filtramos por llaves en el backend
        if (filtrosActuales.idRegistro && filtrosActuales.idRegistro.trim() !== "") {
            paramsApi.llaves = filtrosActuales.idRegistro.trim();
        }

        const data = await bitacoraService.getsBitacoraCambio(paramsApi)
            .catch((err) => {
                console.log("Err: ", err);
                return [];
            });

        // Interpretamos cada fila para extraer su significado de negocio
        let lista = (data || []).map((row) => {
            const interpretacion = interpretarOperacion(row);
            return {
                ...row,
                id: row.idBitacora,
                ...interpretacion,
            };
        });

        // Filtrado en frontend por Tipo de Acción
        if (filtrosActuales.tipoAccion && filtrosActuales.tipoAccion !== "") {
            lista = lista.filter((row) => row.tipoAccion === filtrosActuales.tipoAccion);
        }

        // Filtrado en frontend por ID de Registro
        if (filtrosActuales.idRegistro && filtrosActuales.idRegistro.trim() !== "") {
            const idBuscado = filtrosActuales.idRegistro.trim().toLowerCase();
            lista = lista.filter((row) =>
                String(row.idRegistro).toLowerCase().includes(idBuscado) ||
                (row.llaves && row.llaves.toLowerCase().includes(idBuscado)) ||
                String(row.valorPosterior).toLowerCase().includes(idBuscado) ||
                String(row.valorAnterior).toLowerCase().includes(idBuscado)
            );
        }

        setResults(lista);
        setCargando(false);
    }, [filtros]);

    useEffect(() => {
        fetchResults(filtros);
    }, []);

    const limpiarFiltros = () => {
        const iniciales = {
            idRegistro: "",
            tipoAccion: "",
            tabla: "",
            usuario: "",
            fechaInicio: "",
            fechaFin: "",
        };
        setFiltros(iniciales);
        fetchResults(iniciales);
    };

    const columns = [
        {
            field: 'idBitacora',
            headerName: 'ID',
            width: 55,
            minWidth: 50,
            sortable: true,
            headerAlign: 'center',
            align: 'center',
            ocultarConAuditoria: false,
            renderCell: (p) => (
                <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>
                    {p.value}
                </span>
            )
        },
        {
            field: 'fechaHoraUtc',
            headerName: 'Fecha y Hora',
            width: 155,
            sortable: true,
            headerAlign: 'center',
            align: 'center',
            valueFormatter: (valor) => (valor && moment.utc(valor).isValid() ? moment.utc(valor).local().format("DD/MM/YYYY HH:mm:ss") : "")
        },
        {
            field: 'tituloAccion',
            headerName: 'Operación',
            width: 180,
            sortable: true,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={params.row.colorChip || "default"}
                    size="small"
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                />
            )
        },
        {
            field: 'entidadNombre',
            headerName: 'Entidad / Registro',
            width: 190,
            sortable: true,
            headerAlign: 'left',
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {params.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        ID: {params.row.idRegistro || "N/A"}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'descripcion',
            headerName: 'Descripción de la Acción',
            width: 280,
            flex: 1,
            sortable: false,
            headerAlign: 'left',
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                        {params.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {params.row.cambioLegible}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'usuario',
            headerName: 'Usuario',
            width: 100,
            sortable: true,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value || "SISTEMA"}
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                />
            )
        },
        {
            field: 'acciones',
            headerName: '',
            width: 50,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title="Ver detalle técnico">
                    <IconButton size="small" onClick={() => setDetalleFila(params.row)}>
                        <IconVisibility fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <div>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Trazabilidad de Operaciones del Sistema
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Consulta y seguimiento de operaciones sensibles: anulaciones, asignaciones, consolidaciones, validaciones y cierres.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Filtros de Busqueda */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Grid container spacing={1.5} alignItems="center">
                                {/* Fila 1 */}
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Buscar por ID de Registro"
                                        placeholder="Ej: 14"
                                        value={filtros.idRegistro}
                                        onChange={(e) => setFiltros({ ...filtros, idRegistro: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchResults(filtros); } }}
                                        fullWidth
                                        size="small"
                                        helperText="ID de comprobante, sesión, periodo, etc."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel htmlFor="filtro-tipo-accion">Tipo de Operación</InputLabel>
                                        <Select
                                            native
                                            label="Tipo de Operación"
                                            value={filtros.tipoAccion}
                                            onChange={(e) => setFiltros({ ...filtros, tipoAccion: e.target.value })}
                                            inputProps={{ id: 'filtro-tipo-accion' }}
                                        >
                                            {OPCIONES_TIPO_ACCION.map((op) => (
                                                <option key={op.id} value={op.id}>{op.label}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel htmlFor="filtro-entidad">Módulo / Entidad</InputLabel>
                                        <Select
                                            native
                                            label="Módulo / Entidad"
                                            value={filtros.tabla}
                                            onChange={(e) => setFiltros({ ...filtros, tabla: e.target.value })}
                                            inputProps={{ id: 'filtro-entidad' }}
                                        >
                                            {OPCIONES_ENTIDADES.map((ent) => (
                                                <option key={ent.id} value={ent.id}>{ent.label}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        label="Usuario"
                                        value={filtros.usuario}
                                        onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchResults(filtros); } }}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>

                                {/* Fila 2: Rango de fecha y hora */}
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Fecha y Hora Desde"
                                        type="datetime-local"
                                        value={filtros.fechaInicio}
                                        onChange={(e) => alCambiarFechaInicio(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Fecha y Hora Hasta"
                                        type="datetime-local"
                                        value={filtros.fechaFin}
                                        onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Por defecto hasta las 23:59 del día seleccionado"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            startIcon={<IconSearch />}
                                            onClick={() => fetchResults(filtros)}
                                            fullWidth
                                            size="medium"
                                        >
                                            Buscar
                                        </Button>
                                        <Tooltip title="Limpiar filtros">
                                            <IconButton onClick={limpiarFiltros} size="small">
                                                <IconRestart />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Grilla de Resultados */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <DataGridBase
                            rows={results}
                            columns={columns}
                            loading={cargando}
                            showToolbar={true}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Modal de Detalle Técnico de la Fila */}
            <Dialog
                open={Boolean(detalleFila)}
                onClose={() => setDetalleFila(null)}
                fullWidth
                maxWidth="sm"
                PaperComponent={PaperComponentDraggable}
            >
                <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Detalle de Auditoría
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {detalleFila && (
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Operación</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{detalleFila.tituloAccion}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Fecha y Hora (Local)</Typography>
                                <Typography variant="body2">{moment.utc(detalleFila.fechaHoraUtc).local().format("DD/MM/YYYY HH:mm:ss")}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Tabla</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detalleFila.tabla}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Campo Modificado</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{detalleFila.campo}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">Llaves / Identificadores</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', p: 0.5, borderRadius: 1 }}>
                                    {detalleFila.llaves}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Valor Anterior</Typography>
                                <Typography variant="body2" sx={{ color: 'error.main' }}>
                                    {detalleFila.valorAnterior != null ? String(detalleFila.valorAnterior) : "(Vacío)"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Valor Posterior</Typography>
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    {detalleFila.valorPosterior != null ? String(detalleFila.valorPosterior) : "(Vacío)"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Usuario Responsable</Typography>
                                <Typography variant="body2">{detalleFila.usuario || "(Sistema)"}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">ID Sesión / Request</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                    {detalleFila.requestId || detalleFila.idSesion || "N/A"}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetalleFila(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default OperacionesBitacora;
